import { Box, Button, ButtonBase, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Tooltip, useMediaQuery } from "@mui/material"
import { Add, DeleteOutline, InfoOutlined } from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ListUtil } from "src/framework/commons/ListUtil";
import { useProjetosOffGridStore } from "src/store/vendas/projetos/offgrid.store";
import theme from "src/theme/light";
import { EquipamentoOffGrid, FormProjetoOffGridType } from "../types";
import "./styles.css";
import { confirmationDialog } from "src/framework/store/ConfirmationDialogStore";
import { InputText } from "src/framework/components/forms/inputs/InputText";
import { InputDecimal3 } from "src/framework/components/forms/inputs/InputDecimal3";
import { InputComboBoxList } from "src/framework/components/forms/inputs/InputComboBoxList";

type ModalEquipamentosProps = {
  open: boolean;
  onClose: () => void;
  initialData: EquipamentoOffGrid[];
  onChangeValue: (data: Partial<FormProjetoOffGridType>) => void 
}

interface RowValidations {
  id_interno: string;
  nome: { status: boolean; message: string };
  potencia: { status: boolean; message: string };
  quantidade: { status: boolean; message: string };
  horas_uso: { status: boolean; message: string };
}

interface Row {
  id?: number;
  id_interno: string;
  nome: string | null;
  potencia: string | null;
  quantidade: string | null;
  horas_uso: { id: number, nome: string } | null;
  '@remove'?: boolean
  '@new'?: boolean
}

const formatOption = (value: number) => {
  return {
    id: value,
    nome: `${String(value).replace(".", ",")}h`
  }
}

const horasUsoOptions = ListUtil.createRangeOfValues(0.5, 24.5, 0.5).map(item => formatOption(item))

const addNewRow = (): Row => {
  return {
    id_interno: Math.random().toString(36).substring(2, 15),
    nome: null,
    potencia: null,
    quantidade: null,
    horas_uso: null,
    '@new': true,
  }
}

const EQUIPAMENTOS_MAX_LENGTH = 50

const HORAS_USO_MIN_VALUE = 0.5
const HORAS_USO_MAX_VALUE = 24

const POWER_MIN_VALUE = 1
const POWER_MAX_VALUE = 30_000

const MAX_LINES_SCROLL = 5
const LINE_HEIGHT = 110 // px

const LIMITE_MAXIMO_DIARIO_TOTAL = Number(process.env.REACT_APP_LIMITE_MAXIMO_DIARIO_TOTAL || 0)

const validateRow = (row: Row) => {
  const validations: RowValidations = {
    id_interno: row.id_interno,
    nome: { status: true, message: '' },
    potencia: { status: true, message: '' },
    quantidade: { status: true, message: '' },
    horas_uso: { status: true, message: '' }
  };

  if (row.nome !== null) {
    if (!String(row.nome).trim().length) {
      validations.nome.status = false;
      validations.nome.message = 'Campo obrigatório';
    }
  
    if (row.nome.length > EQUIPAMENTOS_MAX_LENGTH) {
      validations.nome.status = false;
      validations.nome.message = `Máximo de ${EQUIPAMENTOS_MAX_LENGTH} caracteres`;
    }
  }

  if (row.potencia !== null) {
    if (Number(row.potencia || 0) < POWER_MIN_VALUE || Number(row.potencia || 0) > POWER_MAX_VALUE) {
      validations.potencia.status = false;
      validations.potencia.message = `Mínimo ${POWER_MIN_VALUE}W e máximo ${POWER_MAX_VALUE}W`
    }
  }

  if (row.quantidade !== null) {
    if (Number(row.quantidade || 0) < 1) {
      validations.quantidade.status = false;
      validations.quantidade.message = 'Insira um valor de no mínimo 1 unidade';
    }
  }

  if (row.horas_uso !== null) {
    if (Number(row.horas_uso?.id || 0) < HORAS_USO_MIN_VALUE || Number(row.horas_uso?.id || 0) > HORAS_USO_MAX_VALUE) {
      validations.horas_uso.status = false;
      validations.horas_uso.message = `Valor mínimo ${HORAS_USO_MIN_VALUE} e máximo ${HORAS_USO_MAX_VALUE}h`;
  
    }
  }

  return validations;
}

export const ModalEquipamentos = ({ onClose, open, initialData, onChangeValue }: ModalEquipamentosProps) => {
  const { changeModalsValue } = useProjetosOffGridStore(store => store.actions)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const FooterButton = isMobile ? Box : DialogActions;

  const [rows, setRows] = useState<Row[]>(() => {
    const dadosIniciais = initialData.length ? initialData : []
    const dadosValidos = dadosIniciais.filter(item => !item["@remove"]);

    const buildDadosIniciais = () => {
      return dadosIniciais.map((item): Row => ({
        id: item.id,
        id_interno: Math.random().toString(36).substring(2, 15),
        nome: item.nome || null,
        potencia: item.potencia?.toString() || null,
        quantidade: item.quantidade?.toString() || null,
        horas_uso: item.horas_uso ? formatOption(item.horas_uso) : null,
        "@remove": item["@remove"],
        "@new": item["@new"]
      }))
    }

    return dadosValidos.length ? buildDadosIniciais() : [...buildDadosIniciais(), addNewRow()]
  })
  const [validations, setValidations] = useState<RowValidations[]>([])
  
  const validRows = useMemo(() => {
    return rows.filter(item => !item["@remove"])
  }, [rows])
  
  const changeRow = useCallback((rowId: string, field: keyof Row, value: string | number) => {
    setRows(prevRows => {
      const newRows = prevRows.map((row) => {
        return row.id_interno === rowId
        ? { ...row, [field]: value }
        : row
      })

      return newRows;
    })

  }, [])

  useEffect(()=>{
    setValidations(validRows.map(row => validateRow(row)));
  },[validRows])

  const getRowValidation = useCallback((rowId: string): RowValidations | undefined => {
    return validations.find(validation => validation.id_interno === rowId);
  }, [validations])

  const equipamentosInvalidos = useMemo(()=>{
    const anyValueEmpty = validRows.some(row => {
      return Object.values(row).some(value => value === null || (typeof value === 'string' && value.trim() === ''));
    })

    return anyValueEmpty || validations.some(validation => Object.values(validation).some(field => field.status !== undefined && !field.status));
  },[validations, validRows])

  const appendRow = useCallback(() => {
    setRows(prevRows => {
      const newRow = addNewRow();
      return [...prevRows, newRow];
    })
  }, [])

  const removeRow = useCallback((rowId: string) => {
    setRows(prevRows => {
      return prevRows.map(row => {
        if (row.id_interno === rowId) {
          row["@remove"] = true
        }
        return row;
      });
    })
  }, [])

  const getConsumoRow = useCallback((row: Row) => {
    return Number(row.horas_uso?.id || 0) * Number(row.potencia || 0) * Number(row.quantidade || 0);
  }, [])

  const getConsumoTotal = useCallback(() => {
    return validRows.reduce((total, row) => {
      return total + getConsumoRow(row);
    }, 0);
  }, [getConsumoRow, validRows])

  const validateAndSave = useCallback(()=>{
    if (LIMITE_MAXIMO_DIARIO_TOTAL > 0 && getConsumoTotal() > LIMITE_MAXIMO_DIARIO_TOTAL) {
      confirmationDialog.setup({
        title: "Atenção",
        content: (
          <>
            <span>O seu dimensionamento ultrapassou o limite máximo de cálculo,</span><br/>
            <span>caso as informações dos equipamentos estejam corretas, entre</span><br/>
            <span>em contato com sua equipe de atendimento comercial.</span>
          </>
        ),
        onConfirmation: () => confirmationDialog.close(),
        onlyConfirm: true,
        yesText: "Voltar",
        onClose: () => {},
      })
      confirmationDialog.open();
      return;
    }

    onChangeValue({
      recalcular: true,
      isTrocaPotencia: true,
      equipamentos: rows.map(row => {
        return {
          id: row.id,
          "@new": row["@new"],
          "@remove": row["@remove"],
          nome: row.nome || "",
          horas_uso: Number(row.horas_uso?.id || 0),
          potencia: Number(row.potencia || 0),
          quantidade: Number(row.quantidade || 0),
          consumo_diaria: getConsumoRow(row),
        }
      }),
      total_consumo_diario_w: getConsumoTotal() || 0,
      total_consumo_diario_kw: getConsumoTotal() ? (getConsumoTotal() / 1000) : 0,
    })

    changeModalsValue({ equipamentos: false })
  },[getConsumoTotal, onChangeValue, rows, changeModalsValue, getConsumoRow])

  return (
    <Dialog open={open} onClose={(event, reason) => {
      if (reason === 'backdropClick') return ;
      onClose();
    }} maxWidth="xl" className="modal-equipamentos">
      <DialogTitle>Cadastro de equipamentos</DialogTitle>
      <Divider />
      <DialogContent>
        <div style={!isMobile ? { maxHeight: `${MAX_LINES_SCROLL * LINE_HEIGHT}px`, overflowY: "auto" } : {}}>
          {validRows.map((row, index, fullList) => (
            <div className="row X12" style={!isMobile ? { height: `${LINE_HEIGHT}px` } : {}}>
              <div className="X12 M3">
                <InputText
                  label="Nome do equipamento" fullWidth
                  value={row.nome}
                  helperText={getRowValidation(row.id_interno)?.nome?.message}
                  error={!getRowValidation(row.id_interno)?.nome?.status}
                  defaultValue={row.nome}
                  maxLength={EQUIPAMENTOS_MAX_LENGTH}
                  onChange={(e: any) => changeRow(row.id_interno, 'nome', e.target.value)}
                />
              </div>
              <div className="X12 M2">
                <InputDecimal3
                  label={"Potência (W)"}
                  margin="normal"
                  prefix={(
                    <Tooltip title={(
                      <>
                      <span>Exemplo de potência de equipamentos:</span><br/>
                      - Geladeira - 500 W <br/>
                      - Lâmpadas (Led) - 10 W <br/>
                      - TV - 200 W <br/>
                      - Micro-ondas - 1200 W <br/>
                      - Câmera VIP 3220 (5W)  <br/>
                      - Câmera Speed Dome VIP 5225 SD IR (23W) <br/>
                      
                      <span>Observação: As potências acima são exemplo, consulte as informações técnicas do seu equipamento eletrônico.</span>
                      </>
                    )}>
                      <InfoOutlined
                        style={{ marginRight: 5 }}
                        htmlColor={theme.palette.grey[600]}
                        fontSize={"small"}
                      />
                    </Tooltip>
                  )}
                  decimal={0}
                  maxLength={5}
                  allowNegative={false}
                  helperText={getRowValidation(row.id_interno)?.potencia?.message}
                  error={!getRowValidation(row.id_interno)?.potencia?.status}
                  value={row.potencia || ''}
                  onChange={(event: any) => changeRow(row.id_interno, 'potencia', event.target.value || "")}
                />
              </div>
              <div className="X12 M2">
                <InputDecimal3
                  label={"Quantidade"}
                  margin="normal"
                  decimal={0}
                  maxLength={2}
                  allowNegative={false}
                  helperText={getRowValidation(row.id_interno)?.quantidade?.message}
                  error={!getRowValidation(row.id_interno)?.quantidade?.status}
                  value={row.quantidade || ''}
                  onChange={(event: any) => changeRow(row.id_interno, 'quantidade', event.target.value || "")}
                />
              </div>
              <div className="X12 M2">
                <InputComboBoxList
                  id={row.id_interno.replace(/\./g, "-") + "-offgrid-fator-demanda-field"}
                  key={row.id_interno.replace(/\./g, "-") + "-offgrid-fator-demanda-field"}
                  label={"Horas uso no dia"}
                  textField="nome"
                  valueField="id"
                  prefix={(
                    <Tooltip title={"Quantidade de horas que o equipamento deverá permanecer ligado a cada dia."}>
                      <InfoOutlined
                        style={{ marginLeft: 5 }}
                        htmlColor={theme.palette.grey[600]}
                        fontSize={"small"}
                      />
                    </Tooltip>
                  )}
                  error={!getRowValidation(row.id_interno)?.horas_uso?.status}
                  helperText={getRowValidation(row.id_interno)?.horas_uso?.message}
                  options={horasUsoOptions}
                  value={row?.horas_uso || null}
                  renderOptions={(option: any) => {
                    return `${option?.nome}`
                  }}
                  renderOptionsLabel={(option: any) => {
                      return `${option?.nome}`
                    }}
                  onChange={(value) => changeRow(row.id_interno, 'horas_uso', value)}
                />
              </div>
              <div className={`${fullList.length > 1 && (fullList.length - 1) === index ? "X9" : "X10"} M2`}>
                <InputDecimal3
                  label={"Consumo diário (Wh/dia)"}
                  disabled
                  margin="normal"
                  value={getConsumoRow(row)}
                />
              </div>
              <div className={`${fullList.length > 1 && (fullList.length - 1) === index ? "X3" : "X2"} M1`} style={{
                display: "flex",
                alignItems: "flex-start",
                marginTop: 15
              }}>
                {fullList.length > 1 && <Box display={"flex"} alignItems="center" justifyContent={"center"} >
                  <ButtonBase style={{ backgroundColor: theme.palette.error.main, width: 40, height: 40, borderRadius: 5 }} onClick={() => removeRow(row.id_interno)}>
                    <DeleteOutline htmlColor="white" />
                  </ButtonBase>
                </Box>}
                <Box display={"flex"} alignItems="center" justifyContent={"center"} >
                  {(fullList.length - 1) === index && (
                    <ButtonBase style={{ backgroundColor: theme.palette.primary.main, width: 40, height: 40, borderRadius: 5, marginLeft: 5 }} onClick={appendRow}>
                      <Add htmlColor="white" />
                    </ButtonBase>
                  )}
                </Box>
              </div>
            </div>
          ))}
        </div>

        <div className="row X12">
          <div className="X2" />
          <div className="X2" />
          <div className="X2" />
          <div className="X2" />
          <div className="X12 M3">
            <InputDecimal3
              label={"Consumo diário total (Wh/dia)"}
              disabled
              margin="normal"
              value={getConsumoTotal()}
            />
          </div>
          <div className="X1" />
        </div>
      </DialogContent>
      <FooterButton className="footer-modal-edquipamentos">
        <Button onClick={() => changeModalsValue({ equipamentos: false })} color="secondary" variant={isMobile ? "outlined" : "text"} fullWidth={isMobile}>
          Cancelar
        </Button>
        <Button onClick={validateAndSave} disabled={equipamentosInvalidos} color="primary" variant="contained" disableElevation fullWidth={isMobile} style={isMobile ? {marginTop: 15} : {}}>
          Salvar
        </Button>
      </FooterButton>
    </Dialog>
  );
}